import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaGithub, FaExternalLinkAlt, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { projectsData } from './projectsData';
const PortfolioSection = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 500
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.3
      }
    }
  };
  const handleKeyPress = (e) => {
    if (selectedProject) {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') previousImage();
      if (e.key === 'Escape') setSelectedProject(null);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedProject]);

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.gallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const previousImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.gallery.length - 1 : prev - 1
      );
    }
  };


  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: -50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          Featured Projects
        </SectionTitle>

         <ProjectsGrid as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}>
          {projectsData.map((project, index) => (
            <ProjectCard
              key={project.id}
              as={motion.div}
              custom={index}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <ProjectImageWrapper>
                <ProjectImage src={project.image} alt={project.title} />
                <ProjectOverlay
                 as={motion.div}
                 initial={{ opacity: 0 }}
                 whileHover={{ opacity: 1 }}
                 transition={{ duration: 0.2 }}
                >
                  <OverlayContent>
                    <IconButton
                      as={motion.a}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaGithub />
                      
                    </IconButton>
                    <IconButton
                      as={motion.a}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaExternalLinkAlt />
                    </IconButton>
                    <IconButton
                      as={motion.button}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedProject(project)}
                    >
                      <FaInfoCircle />
                    </IconButton>
                  </OverlayContent>
                </ProjectOverlay>
              </ProjectImageWrapper>

              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <TechStack>
                  {project.tech.map((tech, techIndex) => (
                    <TechTag
                      key={tech}
                      as={motion.span}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: techIndex * 0.1 }}
                    >
                      {tech}
                    </TechTag>
                  ))}
                </TechStack>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>






        <AnimatePresence>
          {selectedProject && (
            <ProjectModal
              as={motion.div}
              initial="hidden"
              animate="visible"
              variants={modalVariants}
              exit="exit"
             
            >
              <ModalContent
                as={motion.div}
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                <ModalClose onClick={() => setSelectedProject(null)}>
                  <FaTimes />
                </ModalClose>
                <ModalHeader>
                  <motion.h2
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {selectedProject.title}
                  </motion.h2>
                  <motion.h3
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {selectedProject.subtitle}
                  </motion.h3>
                </ModalHeader>

                <ModalBody>
                  <ModalSection>
                    <SectionTitle>Overview</SectionTitle>
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      {selectedProject.overview}
                    </motion.p>
                  </ModalSection>

                  <ModalSection>
                    <SectionTitle>Challenge</SectionTitle>
                    <ChallengeCard
                      as={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      {selectedProject.challenge}
                    </ChallengeCard>
                  </ModalSection>

                  <ModalSection>
                    <SectionTitle>Solution</SectionTitle>
                    <SolutionCard
                      as={motion.div}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      {selectedProject.solution}
                    </SolutionCard>
                  </ModalSection>

                  <ModalSection>
                    <SectionTitle>Impact</SectionTitle>
                    <ImpactGrid>
                      {Object.entries(selectedProject.impact).map(([key, value], index) => (
                        <ImpactItem
                          key={key}
                          as={motion.div}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7 + index * 0.1 }}
                        >
                          <ImpactIcon>{key}</ImpactIcon>
                          <ImpactValue>{value}</ImpactValue>
                          <ImpactLabel>{key}</ImpactLabel>
                        </ImpactItem>
                      ))}
                    </ImpactGrid>
                  </ModalSection>

                  <ModalSection>
                    <SectionTitle>Tech Stack</SectionTitle>
                    <TechStackGrid>
                      {Object.entries(selectedProject.techStack).map(([category, techs], index) => (
                        <TechStackCategory
                          key={category}
                          as={motion.div}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 + index * 0.1 }}
                        >
                          <CategoryTitle>{category}</CategoryTitle>
                          <TechList>
                            {techs.map((tech, techIndex) => (
                              <TechItem
                                key={tech}
                                as={motion.li}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.9 + techIndex * 0.05 }}
                              >
                                {tech}
                              </TechItem>
                            ))}
                          </TechList>
                        </TechStackCategory>
                      ))}
                    </TechStackGrid>
                  </ModalSection>

                  <ModalSection>
                    <SectionTitle>Gallery</SectionTitle>
                    <GalleryWrapper>
                      <GalleryNavButton onClick={previousImage} position="left">
                        ‹
                      </GalleryNavButton>
                      <GalleryContainer>
                        <AnimatePresence mode="wait">
                          <GalleryImage
                            key={currentImageIndex}
                            as={motion.img}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.3 }}
                            src={selectedProject.gallery[currentImageIndex]}
                            alt={`${selectedProject.title} ${currentImageIndex + 1}`}
                          />
                        </AnimatePresence>
                      </GalleryContainer>
                      <GalleryNavButton onClick={nextImage} position="right">
                        ›
                      </GalleryNavButton>
                    </GalleryWrapper>
                  </ModalSection>
                </ModalBody>
              </ModalContent>
              <ModalOverlay onClick={() => setSelectedProject(null)} />
            </ProjectModal>
          )}
        </AnimatePresence>
      </Container>
    </Section>
  );
};

// Styled Components
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Section = styled.section`
  padding: 8rem 0;
  background: ${({ theme }) => theme.backgroundAlt};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.gradient};
   
  }
`;


const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: ${({ theme }) => theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: ${({ theme }) => theme.gradient};
    border-radius: 2px;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`;

const ProjectCard = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadowMd};
  transition: transform ${({ theme }) => theme.transition};

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProjectImageWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
  overflow: hidden;
`;

const ProjectImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${({ theme }) => theme.transition};
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transition};

  ${ProjectImageWrapper}:hover & {
    opacity: 1;
  }
`;

const OverlayContent = styled.div`
  display: flex;
  gap: 1rem;
`;

const IconButton = styled.a`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transition};

  &:hover {
    transform: scale(1.1);
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  ${({ theme }) => theme.mixins.headingStyles};
  font-size: ${({ theme }) => theme.sizes.xl};
  margin-bottom: 0.5rem;
`;

const ProjectDescription = styled.p`
  ${({ theme }) => theme.mixins.bodyStyles};
  color: ${({ theme }) => theme.textLight};
  margin-bottom: 1rem;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechTag = styled.span`
  font-size: ${({ theme }) => theme.sizes.xs};
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  background: ${({ theme }) => theme.backgroundDark};
  color: ${({ theme }) => theme.textLight};
`;

// Modal Styles
const ProjectModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.card};
  border-radius: 1rem;
  z-index: 1001;
  padding: 2rem;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h2 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes['3xl']};
    margin-bottom: 0.5rem;
  }

  h3 {
    color: ${({ theme }) => theme.textLight};
    font-size: ${({ theme }) => theme.sizes.lg};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ModalSection = styled.div`
  h4 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes.xl};
    margin-bottom: 1rem;
  }
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const ImpactItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${({ theme }) => theme.backgroundDark};
  border-radius: 0.5rem;

  span {
    display: block;
    color: ${({ theme }) => theme.textLight};
    margin-bottom: 0.5rem;
  }

  strong {
    font-size: ${({ theme }) => theme.sizes.lg};
  }
`;

const TechStackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const TechStackCategory = styled.div`
  h5 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes.base};
    margin-bottom: 0.5rem;
    text-transform: capitalize;
  }
`;

const TechList = styled.ul`
  list-style: none;
  padding: 0;
`;

const TechItem = styled.li`
  ${({ theme }) => theme.mixins.bodyStyles};
  color: ${({ theme }) => theme.textLight};
  margin-bottom: 0.25rem;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transition};

  &:hover {
    transform: scale(1.05);
  }
`;
const ChallengeCard = styled.div`
  background: ${({ theme }) => theme.backgroundDark};
  padding: 1.5rem;
  border-radius: 0.5rem;
  border-left: 4px solid ${({ theme }) => theme.primary};
`;

const SolutionCard = styled.div`
  background: ${({ theme }) => theme.backgroundDark};
  padding: 1.5rem;
  border-radius: 0.5rem;
  border-left: 4px solid ${({ theme }) => theme.success};
`;

const ImpactIcon = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 0.5rem;
`;

const ImpactValue = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text};
  margin-bottom: 0.25rem;
`;

const ImpactLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.textLight};
`;

const CategoryTitle = styled.h5`
  color: ${({ theme }) => theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const GalleryWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GalleryContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const GalleryNavButton = styled.button`
  position: absolute;
  ${({ position }) => position}: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;
export default PortfolioSection;